import { useEffect, useState } from 'react'
import { ChevronDown, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useTenantsStore } from '../stores/tenantsStore'

const TenantSelector = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { selectedTenantId, setSelectedTenantId } = useAuthStore()
  const { tenants, fetchTenants } = useTenantsStore()
  const navigate = useNavigate()

  // Fetch tenants on mount
  useEffect(() => {
    fetchTenants()
  }, [fetchTenants])

  // Auto-select first tenant if none selected
  useEffect(() => {
    if (!selectedTenantId && tenants.length > 0) {
      setSelectedTenantId(tenants[0]._id)
    }
  }, [selectedTenantId, tenants, setSelectedTenantId])

  const selectedTenant = tenants.find(t => t._id === selectedTenantId)
  const displayedTenants = tenants.slice(0, 10)

  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenantId(tenantId)
    setIsOpen(false)
  }

  const handleViewAllTenants = () => {
    setIsOpen(false)
    navigate('/tenants')
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('.tenant-selector-dropdown')) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative tenant-selector-dropdown">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
      >
        <div className="flex items-center space-x-2 flex-1">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-lg flex items-center justify-center">
            <Building2 className="h-4 w-4" />
          </div>
          <span className="font-medium truncate">
            {selectedTenant ? selectedTenant.name : 'Select Tenant'}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 ml-auto flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="py-1">
            {/* First 10 Tenants */}
            {displayedTenants.map((tenant) => (
              <button
                key={tenant._id}
                onClick={() => handleTenantSelect(tenant._id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  selectedTenantId === tenant._id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="w-6 h-6 bg-primary-600 text-white rounded flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-3 w-3" />
                </div>
                <span className="truncate">{tenant.name}</span>
              </button>
            ))}

            {/* View All Button */}
            {tenants.length > 10 && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                <button
                  onClick={handleViewAllTenants}
                  className="w-full flex items-center justify-center px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition-colors"
                >
                  View All Tenants ({tenants.length})
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TenantSelector

